import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AssessmentAnswerInterface } from 'app/assessment/models';
import { AppStateInterface } from 'app/core/app-config/state/models';
import {
  EmployeeEquipmentWorkflowInterface,
  EmployeeEquipmentWorkflowStatusInterface,
  EmployeeEquipmentWorkflowStatusTypeInterface,
  EquipmentCategoryInterface,
  EquipmentInterface } from 'app/equipment/models';
import { EquipmentServiceInterface } from 'app/equipment/services/equipment-service.interface';
import {
  LoadEmployeeEquipmentWorkflows,
  LoadEquipmentLibrary,
  LoadEquipmentLibraryItem,
  LoadWorkAreas,
  LoadWorkflowStatusTypes } from 'app/equipment/state/actions';
import { Observable } from 'rxjs';

import {
  AddNewStatusToEmployeeEquipmentWorkflow,
  CreateEmployeeEquipmentWorkflow,
  CreateEquipmentCategory,
  CreateEquipmentLibraryItem } from '../state/actions/equipment-create.action';
import {
  DeleteEmployeeEquipmentWorkflow,
  DeleteEquipmentLibraryCategory,
  DeleteEquipmentLibraryItem } from '../state/actions/equipment-delete.actions';
import {
  UpdateEmployeeEquipmentItem,
  UpdateEmployeeEquipmentWorkFlowStatus,
  UpdateEquipmentCategory,
  UpdateEquipmentLibraryItem } from '../state/actions/equipment-update.action';

import { EquipmentRestConnectorService } from './equipment-rest-connector.service';

@Injectable({
  providedIn: 'root',
})
export class EquipmentService implements EquipmentServiceInterface {

  constructor(private readonly _store: Store<AppStateInterface>,
              private readonly _equipmentRestConnectorService: EquipmentRestConnectorService) {}

  // Main library loader
  public loadLibrary(): void {
    this._store.dispatch(new LoadEquipmentLibrary());
  }

  public getEquipmentLibrary(): Observable<EquipmentCategoryInterface[]> {
    this._store.dispatch(new LoadEquipmentLibrary());

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.equipmentLibrary : []);
  }

  public getRecommendedEquipmentLibrary(): Observable<EquipmentCategoryInterface[]> {
    this._store.dispatch(new LoadEquipmentLibrary());

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.recommendedEquipmentLibrary : []);
  }

  public getLoadingState(): Observable<boolean> {
    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.libraryLoading : false);
  }

  public getEquipmentItem(itemId: number): Observable<EquipmentInterface> {
    this._store.dispatch(new LoadEquipmentLibraryItem(itemId));

    return this._store.select(({ equipment }: AppStateInterface) => equipment? equipment.selectedEquipment : {});
  }

  // Create/Update/Delete library categories
  public createEquipmentCategory(categoryData: EquipmentCategoryInterface): void {
    this._store.dispatch(new CreateEquipmentCategory(categoryData));
  }

  public updateEquipmentCategory(categoryData: EquipmentCategoryInterface): void {
    this._store.dispatch(new UpdateEquipmentCategory(categoryData));
  }

  public deleteEquipmentCategory(categoryData: EquipmentCategoryInterface): void {
    this._store.dispatch(new DeleteEquipmentLibraryCategory(categoryData));
  }

  // Create/Update/Delete library item
  public createEquipmentItem(equipmentItem: EquipmentInterface): Observable<EquipmentInterface> {
    this._store.dispatch(new CreateEquipmentLibraryItem(equipmentItem));

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.selectedEquipment : {});
  }

  public updateEquipmentItem(equipmentItem: EquipmentInterface): void {
    this._store.dispatch(new UpdateEquipmentLibraryItem(equipmentItem));
  }

  public deleteEquipmentItem(equipmentItem: EquipmentInterface): void {
    this._store.dispatch(new DeleteEquipmentLibraryItem(equipmentItem));
  }

  // Load employee equipment workflows
  public loadEmployeeEquipmentWorkflows(userId: number): Observable<EmployeeEquipmentWorkflowInterface[]> {
    this._store.dispatch(new LoadEmployeeEquipmentWorkflows(userId));

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.currentUserEquipmentWorkflows : []);
  }

  // Create/Update employee equipment workflows
  public createEmployeeEquipmentWorkflow(workflowData: EmployeeEquipmentWorkflowInterface): Observable<EmployeeEquipmentWorkflowInterface> {
    this._store.dispatch(new CreateEmployeeEquipmentWorkflow(workflowData));

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.selectedWorkFlow : {});
  }

  public addStatusToEmployeeEquipmentWorkflow(
    workflow: EmployeeEquipmentWorkflowInterface,
    statusToAdd: EmployeeEquipmentWorkflowStatusInterface): Observable<EmployeeEquipmentWorkflowInterface> {
    this._store.dispatch(new AddNewStatusToEmployeeEquipmentWorkflow({
      workflow,
      statusRecord: statusToAdd,
    }));

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.selectedWorkFlow : {});
  }

  public updateEmployeeEquipmentItem(workflowData: EmployeeEquipmentWorkflowInterface): Observable<EmployeeEquipmentWorkflowInterface> {
    this._store.dispatch(new UpdateEmployeeEquipmentItem(workflowData));

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.selectedWorkFlow : {});
  }

  public updateEmployeeEquipmentWorkflowStatus(
    workflow: EmployeeEquipmentWorkflowInterface,
    statusToUpdate: EmployeeEquipmentWorkflowStatusInterface): Observable<EmployeeEquipmentWorkflowInterface> {
    this._store.dispatch(new UpdateEmployeeEquipmentWorkFlowStatus({
      workflow,
      statusRecord: statusToUpdate,
    }));

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.selectedWorkFlow : {});
  }
  // Delete employee equipment workflow
  public deleteEmployeeEquipmentWorkflow(
    workflowToDelete: EmployeeEquipmentWorkflowStatusInterface): Observable<EmployeeEquipmentWorkflowInterface> {
    this._store.dispatch(new DeleteEmployeeEquipmentWorkflow(workflowToDelete));

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.selectedWorkFlow : {});
  }

  // Load accessory data (drop down lists)
  public getEquipmentWorkflowStatusTypes(): Observable<EmployeeEquipmentWorkflowStatusTypeInterface[]> {
    this._store.dispatch(new LoadWorkflowStatusTypes());

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.statusTypes : []);
  }

  public getWorkAreaValues(): Observable<AssessmentAnswerInterface[]> {
    this._store.dispatch(new LoadWorkAreas());

    return this._store.select(({ equipment }: AppStateInterface) => equipment ? equipment.workAreas : []);
  }

  // Equipment export and pdf
  public getEquipmentExportExcel(equipmentIds: number[], exportUrl: string): any {
    return this._equipmentRestConnectorService.loadExcelExportData(equipmentIds, exportUrl);
  }
}
